/**
 * What the file-import panel should be showing, given what was read.
 *
 * Pure, and separate from the DOM for the same reason `blocks-view.ts` is: the
 * decisions worth testing — what the totals are, which rows the preview shows,
 * when an import is refused, what "append" does to two lists — need no browser
 * to be tested in, and `campaign.ts` stays a thin layer of `querySelector`
 * over them.
 *
 * The validation itself is `validatePairs` from `src/domain/`: the same
 * function the server re-runs on submit, so the four numbers on screen cannot
 * disagree with the four numbers that decide what gets billed.
 */

import {
  formatPairedLines,
  IMPORT_SEPARATOR,
  parsePairedLines,
  type ParsedPair,
  type RecipientProblem,
  type RecipientStatus,
} from '../domain/recipients.ts';
import { validatePairs, type ValidationTotals } from '../domain/validation.ts';
import type { TableProblem } from '../domain/table.ts';
import type { XlsxProblem } from '../domain/xlsx.ts';
import { previewSlice } from './preview.ts';

export type ImportFindingName =
  /** Nothing in the file, or nothing but blank rows. */
  | 'import-empty'
  /** One column: almost always the wrong delimiter, occasionally the wrong file. */
  | 'import-single-column'
  /** Longer than `MAX_IMPORT_ROWS`. Refused whole, never trimmed to fit. */
  | 'import-over-limit'
  | 'import-not-a-zip'
  | 'import-no-worksheet'
  | 'import-unsupported-compression';

export interface ImportFinding {
  readonly name: ImportFindingName;
  /** Substituted into the `{placeholders}` of the catalogue message. */
  readonly params: Readonly<Record<string, number>>;
}

export type ImportRow =
  | {
      readonly kind: 'row';
      /** Position in the list, which is the only numbering an appended list can honestly have. */
      readonly index: number;
      readonly phone: string;
      readonly code: string;
      readonly status: RecipientStatus;
      /** The page turns this into a sentence; this module never holds wording. */
      readonly problem?: RecipientProblem;
      readonly params: Readonly<Record<string, number>>;
    }
  /** Stands in for the rows between the head and the tail of a long list. */
  | { readonly kind: 'gap'; readonly hidden: number };

export interface ImportView {
  readonly totals: ValidationTotals;
  readonly rows: readonly ImportRow[];
  readonly findings: readonly ImportFinding[];
  /**
   * Whether this list may be kept. False refuses the whole import: an over-long
   * list trimmed to fit would send a campaign that looks complete and is not.
   */
  readonly accepted: boolean;
}

export interface ImportViewOptions {
  readonly maxRows: number;
  /** A problem the reader hit before there were any rows to validate. */
  readonly problem?: TableProblem | XlsxProblem;
}

/**
 * The panel's whole state for one list.
 *
 * A reader problem and an empty list are not the same thing: the first has
 * something to say, the second is simply a screen nobody has used yet. So an
 * empty list with no problem raises nothing at all.
 */
export function importView(
  pairs: readonly ParsedPair[],
  options: ImportViewOptions,
): ImportView {
  const outcome = validatePairs(pairs, { maxRows: options.maxRows });
  const findings: ImportFinding[] = [];

  const fromReader = readerFinding(options.problem);
  if (fromReader !== undefined) findings.push({ name: fromReader, params: {} });

  if (outcome.overLimit) {
    findings.push({
      name: 'import-over-limit',
      params: { rows: outcome.totals.imported, limit: outcome.limit },
    });
  }

  return {
    totals: outcome.totals,
    rows: windowed(
      outcome.recipients.map((recipient, index) => ({
        kind: 'row' as const,
        index: index + 1,
        // The normalised number where there is one, so a valid row shows what
        // will actually be dialled; the raw text where there is not, because
        // that is the thing in the file that needs correcting.
        phone: recipient.phone === '' ? recipient.rawPhone : recipient.phone,
        code: recipient.promoCode,
        status: recipient.status,
        ...(recipient.problem === undefined ? {} : { problem: recipient.problem }),
        params:
          recipient.duplicateOfLine === undefined ? {} : { line: recipient.duplicateOfLine },
      })),
    ),
    findings,
    // Every finding this panel raises is a refusal — an unreadable file, a
    // single column, a list over the limit — so there is one condition rather
    // than a list of them to keep in step.
    accepted: findings.length === 0,
  };
}

function readerFinding(
  problem: TableProblem | XlsxProblem | undefined,
): ImportFindingName | undefined {
  switch (problem) {
    case 'empty':
      return 'import-empty';
    case 'single-column':
      return 'import-single-column';
    case 'not-a-zip':
      return 'import-not-a-zip';
    case 'no-worksheet':
      return 'import-no-worksheet';
    case 'unsupported-compression':
      return 'import-unsupported-compression';
    default:
      return undefined;
  }
}

function windowed(rows: readonly ImportRow[]): readonly ImportRow[] {
  const slice = previewSlice(rows);
  if (slice.hidden === 0) return slice.head;

  return [...slice.head, { kind: 'gap', hidden: slice.hidden }, ...slice.tail];
}

export type Combine = 'replace' | 'append';

/**
 * What a second import does to the list already on screen (specification §12).
 *
 * Neither answer is safe to assume. Replacing silently would throw away a list
 * somebody spent a morning assembling; appending silently would double a
 * campaign when the same file is dropped twice. So the panel asks, and this is
 * the arithmetic behind each answer.
 */
export function combineImports(
  existing: readonly ParsedPair[],
  incoming: readonly ParsedPair[],
  how: Combine,
): readonly ParsedPair[] {
  return how === 'replace' ? incoming : [...existing, ...incoming];
}

/** The list as it travels in the form field, and back again. */
export function writeImported(pairs: readonly ParsedPair[]): string {
  return formatPairedLines(pairs, IMPORT_SEPARATOR);
}

export function readImported(text: string): readonly ParsedPair[] {
  return parsePairedLines(text, IMPORT_SEPARATOR);
}
